import React from 'react';
import type { FC } from 'react';
import pWaitFor, { TimeoutError } from 'p-wait-for';
import { match } from 'variant';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import { ASTRO_APP_CONFIG_PATH } from '@wix/cli-astro-definitions';
import { CliError, CliErrorCode, normalizeError } from '@wix/cli-error';
import { buildHttpClientHeaders } from '@wix/cli-http-client-react';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { useAsync, useExit } from '@wix/cli-ui-kit';
import {
  getAppInstallUrl,
  HARMONY_DEVELOPMENT_SITE_TEMPLATE_ID,
} from '@wix/cli-site-selector';
import { useDevCenterClient } from '@wix/dev-center-client';
import type { Site } from '@wix/site-list-client';
import { useSiteListClient } from '@wix/site-list-client';
import type { DevelopmentSiteTemplate } from '../dev-site-schema.js';
import { readAppConfig, saveAppConfig } from '../files/app.config.js';
import type { ProjectModel } from '../model.js';
import { formatHumanDevSiteOutput } from './dev-site-output.js';
import type {
  DevSiteOutput,
  SelectionSource,
  SiteOutput,
} from './dev-site-output.js';
import { executeCommand } from './execute-command.js';
import { log } from './logger.js';

export type DevSiteOperation =
  | { type: 'list'; search?: string }
  | { type: 'select'; siteId: string }
  | { type: 'create'; template: DevelopmentSiteTemplate; select: boolean }
  | { type: 'current' };

type RemoteDevSiteOperation = Exclude<DevSiteOperation, { type: 'current' }>;

async function waitForAppToBeInstalled({
  appId,
  isAppInstalled,
}: {
  appId: string;
  isAppInstalled: (appId: string) => Promise<boolean>;
}) {
  const { pollInterval } = getTestOverrides();

  try {
    await pWaitFor(() => isAppInstalled(appId), {
      // Polling interval is set to 6.5 seconds to avoid rate limiting (429)
      interval: pollInterval ?? 6500,
      timeout: 60 * 1000 * 2,
    });
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new CliError({
        code: CliErrorCode.SiteSelectorTimedout(),
        cause: error,
      });
    }

    throw error;
  }
}

function logDevSiteOutput(output: DevSiteOutput, machineOutput: boolean) {
  log(
    machineOutput ? JSON.stringify(output) : formatHumanDevSiteOutput(output)
  );
}

export function logDevSiteError(error: unknown) {
  const normalizedError = normalizeError(error);
  log(
    JSON.stringify({
      event: 'error',
      error: normalizedError.cliCode,
    })
  );
}

async function saveSelectedSite(model: ProjectModel, siteId: string) {
  try {
    await saveAppConfig(model.projectFolder, { siteId });
  } catch (cause) {
    throw new CliError({
      code: CliErrorCode.FailedToSaveDevelopmentSite({
        path: ASTRO_APP_CONFIG_PATH,
      }),
      cause,
    });
  }
}

const ListSites: FC<{
  search?: string;
  onComplete: (output: DevSiteOutput) => void;
}> = ({ search, onComplete }) => {
  const exit = useExit();
  const siteListClient = useSiteListClient();

  useAsync(
    async () => {
      const searchPhrase = search?.trim();
      const sites = await siteListClient.querySites({
        query: {
          ...(searchPhrase ? { filter: { searchPhrase } } : {}),
          cursorPaging: { limit: 100 },
        },
      });

      return {
        event: 'sites',
        sites: sites.map(
          (site): SiteOutput => ({
            siteId: site.id,
            displayName: site.displayName,
            ...(site.viewUrl == null ? {} : { viewUrl: site.viewUrl }),
          })
        ),
      } satisfies DevSiteOutput;
    },
    [],
    { onError: exit, onSuccess: onComplete }
  );

  return null;
};

const SelectSite: FC<{
  appId: string;
  model: ProjectModel;
  siteId: string;
  site?: Site;
  source: SelectionSource;
  onOutput: (output: DevSiteOutput) => void;
  onComplete: (output: DevSiteOutput) => void;
}> = ({ appId, model, siteId, site, source, onOutput, onComplete }) => {
  const exit = useExit();
  const siteListClient = useSiteListClient();
  const devCenterClient = useDevCenterClient({ siteId });

  useAsync(
    async () => {
      const selectedSite = site ?? (await siteListClient.getSite(siteId));
      if (!selectedSite) {
        throw new CliError({
          code: CliErrorCode.DevelopmentSiteNotFound({ siteId }),
          cause: null,
        });
      }

      if (!(await devCenterClient.isAppInstalled(appId))) {
        onOutput({
          event: 'installation_required',
          siteId,
          installUrl: getAppInstallUrl({ appId, siteId }),
        });
        await waitForAppToBeInstalled({
          appId,
          isAppInstalled: devCenterClient.isAppInstalled,
        });
      }

      if (source === 'config') {
        await saveSelectedSite(model, siteId);
      }

      return { event: 'selected', siteId, source } satisfies DevSiteOutput;
    },
    [],
    { onError: exit, onSuccess: onComplete }
  );

  return null;
};

const CreateSite: FC<{
  appId: string;
  model: ProjectModel;
  select: boolean;
  template: DevelopmentSiteTemplate;
  onOutput: (output: DevSiteOutput) => void;
  onComplete: (output: DevSiteOutput) => void;
}> = ({ appId, model, select, template, onOutput, onComplete }) => {
  const exit = useExit();
  const devCenterClient = useDevCenterClient();

  const { status } = useAsync(
    () =>
      template === 'harmony'
        ? devCenterClient.createDevSite({
            templateId: HARMONY_DEVELOPMENT_SITE_TEMPLATE_ID,
          })
        : devCenterClient.createDevSite(),
    [],
    {
      onError: exit,
      onSuccess: (site) => {
        if (!select) {
          onComplete({
            event: 'created',
            siteId: site.id,
            displayName: site.displayName,
            template,
          });
        }
      },
    }
  );

  return match(status, {
    Error: () => null,
    Loading: () => null,
    Success: ({ result: site }) =>
      select ? (
        <SelectSite
          appId={appId}
          model={model}
          site={site}
          siteId={site.id}
          source="config"
          onOutput={onOutput}
          onComplete={onComplete}
        />
      ) : null,
  });
};

const DevSiteOperationComponent: FC<{
  appId: string;
  model: ProjectModel;
  operation: RemoteDevSiteOperation;
  selection?: { siteId: string; source: SelectionSource };
  onOutput: (output: DevSiteOutput) => void;
  onComplete: (output: DevSiteOutput) => void;
}> = ({ appId, model, operation, selection, onOutput, onComplete }) =>
  match(operation, {
    list: ({ search }) => <ListSites search={search} onComplete={onComplete} />,
    select: () => {
      if (!selection) {
        throw new Error(
          'Expected a resolved selection for the select operation'
        );
      }

      return (
        <SelectSite
          appId={appId}
          model={model}
          {...selection}
          onOutput={onOutput}
          onComplete={onComplete}
        />
      );
    },
    create: ({ select, template }) => (
      <CreateSite
        appId={appId}
        model={model}
        select={select}
        template={template}
        onOutput={onOutput}
        onComplete={onComplete}
      />
    ),
  });

async function resolveSelection(
  model: ProjectModel
): Promise<{ siteId: string; source: SelectionSource }> {
  if (model.env.WIX_SITE_ID) {
    return { siteId: model.env.WIX_SITE_ID, source: 'env' };
  }

  const siteId = (await readAppConfig(model.projectFolder))?.siteId;
  if (!siteId) {
    throw new CliError({
      code: CliErrorCode.DevelopmentSiteNotConfigured(),
      cause: null,
    });
  }

  return { siteId, source: 'config' };
}

function resolveOperationSelection(
  model: ProjectModel,
  operation: RemoteDevSiteOperation
): { siteId: string; source: SelectionSource } | undefined {
  if (operation.type === 'select') {
    const envSiteId = model.env.WIX_SITE_ID;
    if (envSiteId && envSiteId !== operation.siteId) {
      throw new CliError({
        code: CliErrorCode.DevelopmentSiteEnvironmentConflict({
          envSiteId,
          requestedSiteId: operation.siteId,
        }),
        cause: null,
      });
    }

    return {
      siteId: operation.siteId,
      source: envSiteId ? 'env' : 'config',
    };
  }

  if (operation.type === 'create' && operation.select) {
    const envSiteId = model.env.WIX_SITE_ID;
    if (envSiteId) {
      throw new CliError({
        code: CliErrorCode.DevelopmentSiteEnvironmentConflict({ envSiteId }),
        cause: null,
      });
    }
  }
}

export async function nonInteractiveDevSite(
  services: AstroCommandServices,
  cliVersion: string,
  model: ProjectModel,
  operation: DevSiteOperation,
  machineOutput: boolean
) {
  if (operation.type === 'current') {
    logDevSiteOutput(
      { event: 'current', ...(await resolveSelection(model)) },
      machineOutput
    );
    return;
  }

  const selection = resolveOperationSelection(model, operation);
  const headers = buildHttpClientHeaders({
    flow: 'Astro_App',
    command: 'dev-site',
    cliVersion,
  });
  const onOutput = (output: DevSiteOutput) =>
    logDevSiteOutput(output, machineOutput);

  const result = await executeCommand<DevSiteOutput>(
    services,
    model,
    (resolve) => (
      <DevSiteOperationComponent
        appId={model.config.appId}
        model={model}
        operation={operation}
        selection={selection}
        onOutput={onOutput}
        onComplete={resolve}
      />
    ),
    { headers }
  );

  logDevSiteOutput(result, machineOutput);
}
